import * as THREE from 'three';

/**
 * Stable ids for every camera mode the game will eventually need. Only
 * DevOrbit is implemented this milestone — the rest are reserved now so
 * gameplay code, save data, and UI can reference a stable id before the
 * implementation exists, instead of every future camera requiring an enum
 * migration.
 */
export enum CameraModeId {
  /** Free-look development/review camera. Not a real gameplay camera. */
  DevOrbit = 'dev-orbit',
  /** Planned: positioned behind the ball looking down the target line. */
  BehindBall = 'behind-ball',
  /** Planned: tracks the ball in flight after a shot. */
  BallFollow = 'ball-follow',
  /** Planned: high overview sweep of a hole, e.g. for course preview. */
  Flyover = 'flyover',
  /** Planned: fixed broadcast-style vantage points around a hole. */
  Spectator = 'spectator',
  /** Planned: scrub/playback camera driven by recorded shot data. */
  Replay = 'replay',
}

/**
 * A pluggable camera behavior. CameraManager holds a registry of these and
 * delegates update/resize to whichever is active; it never needs to know
 * about concrete camera types beyond this interface.
 */
export interface CameraMode {
  readonly id: CameraModeId;
  readonly camera: THREE.Camera;

  /** Called when this mode becomes the active one. */
  activate(): void;
  /** Called when switching away from this mode. */
  deactivate(): void;
  /** Called once per frame while this mode is active. */
  update(deltaSeconds: number): void;
  /** Called on every resize, for all registered modes (not just the active one). */
  handleResize(aspect: number): void;
  /** Release any resources (DOM listeners, controls, etc). */
  dispose(): void;
}
